/**
 * Pure reducer that derives the agentic-room view state from an ordered
 * list of AppSync envelopes plus locally-staged "optimistic" entries.
 *
 * Why a pure reducer (and not a hook with refs):
 *   - `useAgentChannel` already enforces dedupe + ordering, and the buffer
 *     is bounded (`trimChannelBuffer`: stream envelopes capped, lifecycle never evicted). Walking it on
 *     every render is O(buffer) and trivially memoizable, but the value
 *     of having a *pure* function is unit-test parity: every state we
 *     describe in `Composer` / `AgenticNetworkRoom` can be reproduced from
 *     a fixture array of envelopes with zero React.
 *   - Phase 2 will extend the output with `turnGroups[].thinking[]` for
 *     the bounded "Thinking" stream. Because the reducer is pure, that
 *     change is additive and won't perturb Phase 1 callers.
 *
 * Inputs:
 *   - `envelopes`     — chronological envelopes from `useAgentChannel`.
 *   - `optimistic`    — local "user bubble" stand-ins inserted before the
 *                       backend echoes a `USER_PROMPT` for the same run.
 *                       Reconciled by `correlationId`.
 *
 * Outputs:
 *   - `feed`          — ordered display entries (`kind: 'user'|'agent'|'system'`).
 *   - `uiState`       — last seen `UI_STATE.uiState` (`IDLE` / `RUNNING` /
 *                       `AWAITING_APPROVAL` / `COMPLETED` / `FAILED`).
 *   - `pendingApproval` — the most recent unresolved approval request
 *                       carrying the `taskToken` + `taskId` we need to
 *                       resolve via `pool_approval_handler`.
 *   - `lastExecution` — `{ executionId, startedAt, status }` reflecting the
 *                       most recent EXECUTION_* envelope; lets the composer
 *                       render the correct state during the brief window
 *                       between EXECUTION_STARTED and the first UI_STATE.
 */
import { AGENT_EVENTS, envelopeEventType, UI_STATES } from '../constants/agentEvents.js';
import { envelopeToThinkingLines } from './thinkingLine.js';
import {
  MODEL_ROWS, MODEL_ID_TO_FAMILY, MODEL_PRICING,
  CACHE_READ_MULTIPLIER, CACHE_CREATION_MULTIPLIER,
} from '../services/generatedPricing';

/** Resolve the rate/points row for a model id — family-by-substring fallback (sonnet default),
 *  identical to BuilderStreamTerminal so the run-total matches the build footer exactly. */
function _rowForModel(model) {
  const direct = model ? MODEL_ID_TO_FAMILY[model] : null;
  if (direct) return { rates: MODEL_PRICING[direct], pts: MODEL_ROWS[direct] };
  const m = String(model || '').toLowerCase();
  const fam = m.includes('haiku') ? 'haiku' : m.includes('opus') ? 'opus' : 'sonnet';
  return { rates: MODEL_PRICING[fam], pts: MODEL_ROWS[fam] };
}

/** Compute {estimatedCostUsd, points} from a token count + model, using the ONE rate sheet the
 *  build footer + run-total share. Shared by the streamed-line reconstruction and the authoritative
 *  builder usage chip (which arrives tokens-only), so both produce cost the run-total can sum. */
function _costPointsFromTokens({ input = 0, output = 0, cacheRead = 0, cacheCreation = 0 }, model) {
  const { rates, pts } = _rowForModel(model);
  const estimatedCostUsd =
    (input / 1_000_000) * rates.input
    + (output / 1_000_000) * rates.output
    + (cacheRead / 1_000_000) * (rates.input * CACHE_READ_MULTIPLIER)
    + (cacheCreation / 1_000_000) * (rates.input * CACHE_CREATION_MULTIPLIER);
  const points = Math.round(
    input * pts.pts_input
    + output * pts.pts_output
    + cacheRead * pts.pts_input * CACHE_READ_MULTIPLIER
    + cacheCreation * pts.pts_input * CACHE_CREATION_MULTIPLIER,
  );
  return { estimatedCostUsd, points };
}

/** Ensure a builder ``usage`` chip carries ``estimatedCostUsd`` + ``points`` (the fields
 *  ``computeRunTotal`` sums). The authoritative chip from ``BUILDER_PHASE_COMPLETED.usage`` is
 *  tokens-only; without this it would contribute $0 to the run-total. Idempotent — a chip that
 *  already has a cost (e.g. reconstructed from lines) is returned unchanged. */
function withBuilderCost(usage) {
  if (!usage || typeof usage !== 'object') return usage;
  if (usage.estimatedCostUsd != null || usage.cost != null) return usage;
  const { estimatedCostUsd, points } = _costPointsFromTokens({
    input: Number(usage.inputTokens) || 0,
    output: Number(usage.outputTokens) || 0,
    cacheRead: Number(usage.cacheReadTokens) || 0,
    cacheCreation: Number(usage.cacheCreationTokens) || 0,
  }, usage.modelId);
  return { ...usage, estimatedCostUsd, points };
}

/** Build a WORKER-shaped usage chip ({modelId, estimatedCostUsd, points, tokens}) from a builder
 *  row's own cc-stream (``builderLines``). A builder's cost is streamed as ``cc:'usage'`` events
 *  (and a session-total ``cc:'result'``) — the SAME source the build footer's StatusBar uses — but
 *  it was never turned into a ``row.usage`` chip, so the run-total (which sums ``row.usage`` for
 *  every worker/lead/reducer) silently omitted builders. This closes that gap with the identical
 *  rate sheet + formula the footer uses. Returns null when the stream carried no usage. */
function builderUsageFromLines(lines, modelHint) {
  if (!Array.isArray(lines) || lines.length === 0) return null;
  let model = modelHint || '';
  let input = 0, output = 0, cacheRead = 0, cacheCreation = 0;
  let result = null;
  for (const e of lines) {
    if (!e || typeof e !== 'object') continue;
    if (e.cc === 'init' && !model && typeof e.model === 'string') model = e.model;
    else if (e.cc === 'usage') {
      input += Number(e.input) || 0;
      output += Number(e.output) || 0;
      cacheRead += Number(e.cacheRead) || 0;
      cacheCreation += Number(e.cacheCreation) || 0;
    } else if (e.cc === 'result') {
      result = e;
      // An avatar reduce runs with no explicit model, so its cc:init.model is empty; the harness
      // stamps the model the RESPONSE used on cc:result instead. Honor it so the lines-derived chip
      // names the model (parity with a network agent), not just the authoritative usage_snapshot.
      if (!model && typeof e.modelId === 'string') model = e.modelId;
    }
  }
  // Prefer the de-duplicated session-aggregate on ``cc:'result'`` when present (matches StatusBar).
  if (result && result.totalInput != null) {
    input = Number(result.totalInput) || 0;
    output = Number(result.totalOutput) || 0;
    cacheRead = Number(result.totalCacheRead) || 0;
    cacheCreation = Number(result.totalCacheCreation) || 0;
  }
  if (!(input || output || cacheRead || cacheCreation)) return null;
  const { estimatedCostUsd, points } = _costPointsFromTokens(
    { input, output, cacheRead, cacheCreation }, model,
  );
  return {
    modelId: model || undefined,
    inputTokens: input, outputTokens: output,
    cacheReadTokens: cacheRead, cacheCreationTokens: cacheCreation,
    estimatedCostUsd, points,
  };
}

/**
 * @typedef {Object} AgentUsageSnapshot
 * @property {string} modelId
 * @property {number} inputTokens
 * @property {number} outputTokens
 * @property {number} cacheReadTokens
 * @property {number} cacheCreationTokens
 * @property {number} calls            Number of Bedrock invocations
 *   accumulated for this turn (initial + tool-continuation rounds).
 * @property {number} estimatedCostUsd Float USD; AWS-cost (what we pay
 *   Bedrock), not customer-billed value. Used for the room chip.
 * @property {number} points           Customer-facing points consumed,
 *   computed via ``stepflow_core.pricing.v1.points_for_llm``.
 */

/**
 * @typedef {Object} FeedEntry
 * @property {'user'|'agent'|'system'} kind
 * @property {string} id              Stable id; uses eventId or local bubble id.
 * @property {string} [eventId]       Backend envelope id when present.
 * @property {string} [correlationId] Used to reconcile optimistic user bubbles.
 * @property {string} [agentName]
 * @property {string} text
 * @property {number|string} [ts]
 * @property {AgentUsageSnapshot} [usage]  Per-turn Bedrock usage for
 *   agent entries; absent for user/system rows or for agents that
 *   didn't call Bedrock.
 */

/**
 * @typedef {Object} PendingApproval
 * @property {string} taskId
 * @property {string} taskToken
 * @property {string} [agentName]
 * @property {Object} [design]
 * @property {string} [eventId]
 */

/**
 * @typedef {Object} LastExecution
 * @property {string} executionId
 * @property {'RUNNING'|'COMPLETED'|'FAILED'} status
 * @property {string|number} [startedAt]
 * @property {string|number} [endedAt]
 */

/**
 * @typedef {Object} OptimisticEntry
 * @property {string} id
 * @property {string} text
 * @property {string} correlationId
 * @property {string|number} [ts]
 * @property {string} [actorName]
 */

/**
 * @typedef {import('./thinkingLine.js').ThinkingLine} ThinkingLine
 */

/**
 * @typedef {Object} TurnGroup
 * @property {string} id              Stable id (correlationId or first-event id).
 * @property {string} [correlationId]
 * @property {string} [executionId]  SFN execution name — used to attach
 *   late ``LOG_*`` after the final response to the right card (same
 *   ``TurnGroup``), instead of opening a second turn.
 * @property {FeedEntry|null} userEntry
 * @property {FeedEntry[]} agentEntries  Final agent responses + system rows
 *                                       belonging to this turn (in order).
 * @property {ThinkingLine[]} thinking   Full ordered transcript (the UI
 *                                       tail-slices the live one, expands on click).
 * @property {Array<{cc: string, [key: string]: any}>} builderTerminalLines  Structured Claude Code
 *   terminal events (`LOG_LINE` ``payload.stream === "universal_builder"``).
 * @property {string|undefined} activeAgentId Most recent log row's agentId
 *                                       while the turn is live; cleared on close.
 * @property {Object|null} builderCompletion Legacy single builder-run summary
 *   (one-per-turn; last-wins). Used for old executions whose envelopes carry
 *   no agentId. Prefer ``builderCompletions`` when non-empty.
 * @property {Array<Object>} builderCompletions Per-agent builder-run summaries
 *   { agentId, exitCode, logKey, eventsKey, mode, ... }, keyed/deduped by
 *   agentId. Drives one BuilderLogCard per agent, each loading its own log.
 * @property {'live'|'completed'|'failed'} status
 * @property {string} [runId]  The top-level run this turn belongs to (== the run's EXECUTION_STARTED
 *   executionId). The room folds all turns whose ``runId === currentRunId`` into one hierarchy tree.
 * @property {string|number} [startedAt]
 * @property {string|number} [endedAt]
 */

/**
 * @typedef {Object} AgentRosterRow
 * @property {string} agentId
 * @property {string} [label]           Display name (defaults to agentId).
 * @property {number} [level]           1-based depth (root's children = 1).
 * @property {string} [parentAgentId]   Enclosing agent's id (null/absent for level-1 rows).
 * @property {string} [nodePath]        ``root/Parent/Child`` — full tree path.
 * @property {'queued'|'running'|'awaiting'|'done'|'failed'} status  Monotonic (see AGENT_STATUS_RANK).
 * @property {boolean} [isBuilder]      True once a builder phase/wait names this agent.
 * @property {string} [runId]           The builder's runId (for the Stop control).
 * @property {Object} [usage]           Bedrock usage chip from AGENT_FINAL_RESPONSE.
 * @property {string} [text]            The agent's final text (loaded into the shared panel on click).
 * @property {Array<Object>} [builderLines]  Per-agent live cc-stream (terminal lines) — the shared
 *   container renders these via BuilderStreamTerminal when a RUNNING builder row is selected.
 * @property {Object} [builderCompletion]    Per-agent completed-run summary ({logKey, eventsKey,
 *   exitCode, ...}) — the container renders BuilderLogCard (S3 transcript) for a DONE builder.
 * @property {Array<{stage: string, text: string}>} [reasoning]  Attributed AGENT_REASONING for this
 *   node — the lead's routing rationale (stage 'lead') and/or its node-reducer judge verdict (stage
 *   'reducer'). Rendered as a collapsible block above the row's output in the shared reader.
 * @property {number} [seq]             First-seen ordinal — the tie-break for same-level rows.
 */

/**
 * @typedef {Object} AgentRoomState
 * @property {FeedEntry[]} feed
 * @property {TurnGroup[]} turnGroups
 * @property {string|null} uiState
 * @property {PendingApproval|null} pendingApproval
 * @property {LastExecution|null} lastExecution
 * @property {string|undefined} activeAgentId   Convenience pointer at the
 *                                              tail of the latest live turn's
 *                                              activeAgentId — what MiniFlow
 *                                              highlights.
 * @property {Array<Object>} pendingBuilderWaits  ALL unresolved builder input waits
 *   ({agentId, runId, waitEventId, prompt, interaction, turnId}) — the room shows an "N awaiting"
 *   notice + binds the interaction band to the one the user selects. Empty when none are pending.
 * @property {Object<string, AgentRosterRow>} agents  Run-level hierarchy roster keyed by agentId
 *   (event-accreted, dynamic — rows appear as events arrive). Reset each top-level run.
 * @property {AgentRosterRow[]} agentRoster  ``agents`` as a stable array ordered by (level, seq)
 *   — the room renders one non-expanding line per row and derives the tree from parentAgentId.
 * @property {string|null} currentRunId  The latest top-level run id. Turns with ``runId ===
 *   currentRunId`` are the current run (rendered as ONE tree); older turns stay flat cards.
 */

const DEFAULT_STATE = Object.freeze({
  feed: Object.freeze([]),
  turnGroups: Object.freeze([]),
  uiState: null,
  pendingApproval: null,
  lastExecution: null,
  activeAgentId: undefined,
  // ALL currently-unresolved builder input waits (not just the latest — a generative fan-out
  // can pause several builders at once). Each: { agentId, runId, waitEventId, prompt,
  // interaction }. The room shows an "awaiting" chip per agent + an "N awaiting" notice, and
  // binds the interaction band to the ONE the user selects. Resolved entries are removed.
  pendingBuilderWaits: Object.freeze([]),
  agents: Object.freeze({}),
  agentRoster: Object.freeze([]),
  currentRunId: null,
  currentRunReleaseVersion: null,
  runReleaseVersions: Object.freeze({}),
  agentRostersByRun: Object.freeze({}),
});

/**
 * A turn opens on USER_PROMPT (or EXECUTION_STARTED if no prompt is yet in
 * the buffer) and closes on AGENT_FINAL_RESPONSE or terminal EXECUTION_*.
 *
 * We keep the most recent *open* turn in ``liveTurn`` and append everything
 * that arrives between open and close — thinking lines accumulate, and the
 * activeAgentId tracks the tail.
 */
function newTurn({ id, correlationId, userEntry, startedAt, executionId }) {
  return {
    id,
    correlationId,
    executionId: executionId || undefined,
    userEntry: userEntry || null,
    agentEntries: [],
    thinking: [],
    builderTerminalLines: [],
    builderPhases: [],
    // The builder's OWN runId (runner execution-name), captured from
    // BUILDER_PHASE payload.runId. Distinct from ``executionId``; this is what
    // the Stop button must send so the cancel row lands where the builder polls.
    builderRunId: undefined,
    builderInputWait: null,
    builderCompletion: null,
    // Per-agent builder completions for a multi-agent turn. Each entry is a
    // builder-run summary { agentId, exitCode, logKey, eventsKey, mode, ... }
    // keyed by agentId so the room renders ONE card per agent (each loading
    // its OWN transcript). ``builderCompletion`` above stays as the legacy
    // single-card field for old executions whose envelopes carry no agentId.
    builderCompletions: [],
    // Wire-timestamp of the FIRST BUILDER_PHASE seen for this turn.
    // Used by ``TurnGroup`` to slot the Builder surface chronologically
    // among other agents' final-response panels (e.g. Lead → Builder →
    // Test should render in that order, even though Lead's
    // AGENT_FINAL_RESPONSE landed before any builder phases).
    builderStartedAt: undefined,
    activeAgentId: undefined,
    // Set of agent ids that have already had a turn this run (i.e. were
    // ``activeAgentId`` and then handed off). MiniFlow paints these in a
    // distinct "completed" tint so the user sees the network's progress
    // rather than only the single currently-active node.
    completedAgentIds: new Set(),
    status: 'live',
    startedAt,
    endedAt: undefined,
  };
}

// Monotonic status ranks — a late/duplicate/reconnect event can never regress a roster row
// (e.g. a stray EXECUTION_STARTED arriving after the agent already finished). ``awaiting`` and
// ``running`` share a rank: a builder toggles between them freely until it's ``done``/``failed``.
// ``degraded`` shares the terminal rank (2) with done/failed — a governed-stop completion is
// terminal and must not be regressed by a late event, nor regress a real done/failed.
const AGENT_STATUS_RANK = { queued: 0, running: 1, awaiting: 1, done: 2, degraded: 2, failed: 2 };

/** Upsert a roster row on the run-level ``roster`` map by agentId, monotonic on status. Never
 *  regresses. ``patch`` may carry any of {label, level, parentAgentId, nodePath, status, isBuilder,
 *  usage, text, runId}. Unknown/blank agentId → no-op. */
/** f.synth IDENTITY MERGE (L63 follow-up, 2026-09-19): the room ALREADY holds one synthesis row —
 *  the engine-stage ``__reducer__root`` (rendered "Network Summarizer", the run's synthesis
 *  heartbeat). The container's live claude-code reduce streams under the DISPLAY id
 *  "Network Summarizer" (kept on the wire for billing/log readability), which upserted a SECOND
 *  roster row with the same visible label + a builder chip — two rows for the SAME actor. Key the
 *  reduce's builder events onto the existing row so heartbeat → live terminal → done is ONE line.
 *  (n.synth's "n.synth · <agent>" ids stay their own rows — per-node judges have no level-1 twin.) */
function reducerRosterKey(agentId) {
  // Both f.synth wire identities → the one existing row: the container's live-stream display id
  // AND the engine's terminal log-card id (_publish_reducer_log emits agentId="f.synth").
  return (agentId === 'Network Summarizer' || agentId === 'f.synth') ? '__reducer__root' : agentId;
}

/** TRUE when a PLAN_UPDATE_RUNNING/PLAN_UPDATED beacon does NOT belong to the current run, so the
 *  Planner roster row must not be created/settled from it. The engine stamps the run on the beacon
 *  (correlationId → executionId → payload.runId); ``currentRunId`` is EXECUTION_STARTED's id (verified
 *  equal to the stamp). Off-run when the run is known AND the stamp differs — which INCLUDES an
 *  unstamped legacy beacon (blank stamp), necessarily from a pre-stamping prior run, so it can't leak
 *  a stale "Planner done" into a fresh run. currentRunId unknown (historical room) ⇒ can't scope ⇒
 *  not off-run (honor it). Mirrors ``latestPlanBeacon`` on the room side. */
function _planBeaconOffRun(env, payload, currentRunId) {
  const cur = String(currentRunId || '').trim();
  if (!cur) return false;
  const beaconRun = String(
    (env && (env.correlationId || env.executionId))
    || (payload && payload.runId) || '',
  ).trim();
  return beaconRun !== cur;
}

function upsertAgentRow(roster, agentId, patch) {
  const id = String(agentId || '').trim();
  // The async Planner row is flagged wherever it's born (beacon OR its builder stream arriving
  // first on reconnect) — agentTree needs isPlanner for root placement below Network Summarizer.
  if (id === 'Planner' && patch && !patch.isPlanner) patch = { ...patch, isPlanner: true };
  // ROOT-REDUCER LABEL (2026-09-22): both wire identities map to __reducer__root — the container's
  // live stream sends the friendly "Network Summarizer", but the engine's TERMINAL log-card emits
  // the INTERNAL "f.synth". Without normalizing, the row rendered "Network Summarizer" while running
  // then flipped to "f.synth" (an internal name) on completion. Pin the display label here — the one
  // chokepoint every reducer upsert passes through — so the UX name never regresses to the wire id.
  if (id === '__reducer__root' && patch && 'label' in patch) {
    patch = { ...patch, label: 'Network Summarizer' };
  }
  if (!id || !roster) return;
  const prev = roster[id] || { agentId: id, status: 'queued', seq: Object.keys(roster).length };
  const next = { ...prev, ...patch, agentId: id };
  // Monotonic status guard.
  const ns = patch && patch.status;
  // STAGE → BUILDER HANDOFF (L93): ``__reducer__root`` is shared by TWO phases — the root node-judge
  // (an engine-stage reasoning line, flipped ``done`` after layer 1) and, a minute later, f.synth
  // streaming as a builder. The guard kept the judge's ``done``, so f.synth's live terminal never
  // opened (Planner — its own fresh row — streamed fine). The FIRST builder beat on a done
  // non-builder stage row is a NEW phase → reopen once; after that the row is a builder and the guard
  // holds (a late line can't reopen a finished f.synth).
  const stageHandoff = ns === 'running' && patch.isBuilder === true
    && prev.isEngineStage === true && !prev.isBuilder;
  if (ns && prev.status && !stageHandoff
      && (AGENT_STATUS_RANK[ns] ?? 0) < (AGENT_STATUS_RANK[prev.status] ?? 0)) {
    next.status = prev.status;                 // keep the more-advanced status
  }
  roster[id] = next;
}

/** Tree placement + clean label for an engine-STAGE row (Lead router / Reducer judge) derived from
 *  its ``nodePath`` + ``stage``. The stage row nests UNDER the node that owns the cohort (the last
 *  path segment) so the tree reads: Fundamentals Lead → { Lead, …specialists…, Summarizer }. The
 *  root cohort's own Lead/Reducer sit at the top as "Network Lead" / "Network Summarizer".
 *
 *  ``lead root``                       → { label:'Network Lead',       parent:null,               level:1 }
 *  ``lead root/Fundamentals Lead``     → { label:'Lead',               parent:'Fundamentals Lead', level:2 }
 *  ``reducer root/Fundamentals Lead``  → { label:'Summarizer',         parent:'Fundamentals Lead', level:2 }
 *  ``reducer root``                    → { label:'Network Summarizer', parent:null,               level:1 } */
function engineStagePlacement(stage, nodePath) {
  const segs = String(nodePath || 'root').split('/').filter((s) => s && s !== 'root');
  const owner = segs.length ? segs[segs.length - 1] : null;
  const isReducer = stage === 'reducer';
  if (!owner) {
    return { label: isReducer ? 'Network Summarizer' : 'Network Lead', parentAgentId: undefined, level: 1 };
  }
  return {
    label: isReducer ? 'Summarizer' : 'Group Lead',
    parentAgentId: owner,          // nest under the node whose cohort this stage routes/judges
    level: segs.length + 1,        // one level below that node
  };
}

/** Update a turn's active agent. When the active agent changes, the
 *  prior agent joins the ``completedAgentIds`` set — that's the signal
 *  MiniFlow uses to paint past nodes a different colour. */
function setActiveAgent(turn, nextAgentId) {
  if (!turn || !nextAgentId) return;
  const prev = turn.activeAgentId;
  if (prev && prev !== nextAgentId) {
    if (!(turn.completedAgentIds instanceof Set)) {
      turn.completedAgentIds = new Set();
    }
    turn.completedAgentIds.add(prev);
  }
  turn.activeAgentId = nextAgentId;
}

// Sub-execution SFN markers. The generative engine dispatches each agent as a ``.sync`` child of
// ``stepflow-universal-worker``/``-builder``; the SFN→AppSync bridge publishes EXECUTION_* for
// THOSE children too (not just the top-level run). We must ignore child EXECUTION_STARTED/COMPLETED/
// FAILED for run-lifecycle purposes (roster reset, currentRunId, run-terminal close) — else a later
// layer's children wipe earlier layers mid-run and the first child's completion ends the run early.
const _SUBEXEC_ARN_MARKERS = ['universal-worker', 'universal-builder'];

/** True when an EXECUTION_* envelope belongs to a generative agent SUB-execution (a ``.sync`` child)
 *  rather than the TOP-LEVEL run. Decided by the SFN ARN the bridge stamps on the payload (falls
 *  back to the envelope). Unknown/missing ARN → treated as top-level (safe: preserves the old
 *  single-run behavior for classic ASL networks that don't fan out). */
function isSubExecutionStarted(payload, env) {
  const arn = String(
    (payload && payload.stateMachineArn)
    || (env && env.stateMachineArn)
    || '',
  );
  if (!arn) return false;
  return _SUBEXEC_ARN_MARKERS.some((m) => arn.includes(m));
}

/** Coerce a payload to `{}` so accessors don't throw on legacy envelopes. */
function payloadOf(env) {
  return env && typeof env.payload === 'object' && env.payload !== null ? env.payload : {};
}

function tsOf(env) {
  if (!env) return undefined;
  return env.timestamp ?? env.created_at ?? env.serverTimestamp ?? env.server_timestamp;
}

/**
 * Normalize an approval `design` to an object regardless of wire shape.
 * The live AppSync APPROVAL_REQUESTED event carries `design` as a resolved
 * object (the worker runs `load_artifact` → `json.loads` before publishing),
 * but the REST restore path (`GET /networks/{id}/executions/{taskId}`) returns
 * it `json.dumps`'d as a STRING. The brain reducer hit exactly this drift —
 * the diagram silently vanished on reload because `graphFromDesign` rejected a
 * string. Nothing in the room renders a diagram from `pendingApproval.design`
 * yet, so this is a tripwire, not a live bug: parse-or-keep here so the moment
 * a room approval surface starts reading `design` it can't regress the same way.
 */
function designOf(raw) {
  if (raw && typeof raw === 'object') return raw;
  if (typeof raw === 'string' && raw.length > 0) {
    try { return JSON.parse(raw); } catch { return undefined; }
  }
  return undefined;
}

/**
 * @param {Array<Record<string, unknown>>} envelopes
 * @param {{ optimistic?: OptimisticEntry[] }} [opts]
 * @returns {AgentRoomState}
 */
export function reduceAgentRoomState(envelopes, opts = {}) {
  if (!Array.isArray(envelopes) || envelopes.length === 0) {
    if (!opts.optimistic || opts.optimistic.length === 0) return DEFAULT_STATE;
  }
  // KICK-SEEDED run id (opts.pendingRunId): the executionName the room got back from ``poolExecute``
  // when it kicked THIS run. An event carrying this id is authoritatively the new run, so its FIRST
  // event force-starts the block even if ``currentRunId`` still points at the prior run and the
  // run-start beacon (USER_PROMPT / EXECUTION_STARTED) is delayed/reordered — the artifact-delayed-kick
  // standby race (see adoptRunId). '' when no local kick (peer/other-client runs are unaffected).
  const pendingRunId = String(opts.pendingRunId || '').trim();

  /** @type {FeedEntry[]} */
  const feed = [];
  /** @type {TurnGroup[]} */
  const turnGroups = [];
  /** @type {TurnGroup|null} */
  let liveTurn = null;
  /** @type {string|null} */
  let uiState = null;
  /** @type {PendingApproval|null} */
  let pendingApproval = null;
  /** @type {LastExecution|null} */
  let lastExecution = null;
  // ALL currently-unresolved builder input waits, keyed by runId. A generative fan-out can pause
  // several builders at once; the room shows an "awaiting" chip per agent, an "N awaiting" notice,
  // and binds the interaction band to the ONE the user picks. Entries are added on
  // BUILDER_INPUT_WAIT and removed on the matching BUILDER_INPUT_RESOLVED (keyed by runId — the
  // RESOLVED payload carries no agentId, so runId is the join key). Value:
  // { agentId, runId, waitEventId, prompt, interaction, turnId }.
  /** @type {Map<string, Object>} */
  const pendingBuilderWaits = new Map();
  // RUN-LEVEL hierarchy roster (event-accreted, like the architect's buildActivity.agents): a map
  // keyed by agentId → AgentRosterRow. The generative fan-out closes a TURN per agent final (each
  // worker is its own sub-execution), so the tree can't live on a turn — it's per-RUN (one user
  // prompt). Rows APPEAR as their events arrive and are placed in the tree by parentAgentId/level.
  // Reset when a new top-level run starts (USER_PROMPT / EXECUTION_STARTED) so the roster always
  // reflects the CURRENT run; on REST replay it naturally settles to the last run's tree.
  let roster = {};
  // PER-RUN rosters {runId → roster map}. Each run gets its OWN tree (same network, different route
  // per run; topology may even differ across MAJOR versions). ``roster`` above is the CURRENT run's
  // working map; on each new top-level run we snapshot the outgoing one here (keyed by its runId) so
  // history keeps every run's tree intact. The room renders one collapsible block per run.
  const rostersByRun = {};
  // ENGINE-STAGE COST {runId → {lead:{cost,points,calls}, reducer:{…}}}. The Lead + node-reducer
  // publish their per-call LLM usage on AGENT_REASONING (payload.usage, keyed by ``stage``); we SUM
  // it per run per stage so the room can show "All Leads $X" / "All Reducers $Y" cost lines. The
  // Final synthesis + workers carry usage on their own AGENT_FINAL_RESPONSE (rendered per-row), so
  // this map is ONLY the two ambient engine stages that have no roster row of their own.
  const stageUsageByRun = {};
  // Id of the CURRENT (latest) top-level run — the executionId of the last top-level
  // EXECUTION_STARTED. Every turn opened during that run is stamped ``turn.runId = currentRunId``
  // so the room can render the current run as ONE hierarchy tree while older runs stay flat cards.
  let currentRunId = null;
  // Ids of SUB-executions seen this stream (each generative worker/builder ``.sync`` child, keyed by
  // its own executionId). The bridge publishes lifecycle events — including a room ``UI_STATE`` — for
  // these children too, and a UI_STATE envelope carries NO stateMachineArn (so the ARN-based
  // ``isSubExecutionStarted`` can't catch it). We learn a child's id from its ARN-tagged
  // EXECUTION_STARTED (which DOES carry the universal-* ARN) and then IGNORE any UI_STATE stamped with
  // that id — a child's IDLE is not the room's terminal (frank-beaver done-early bug, LESSONS L18).
  const subExecIds = new Set();
  // Run ids whose FINAL synthesizer (agentName 'Summary' — the generative engine's last node) has
  // landed. Marks a run as truly DONE even if its terminal beacon (EXECUTION_COMPLETED / UI_STATE)
  // was dropped, so the settle-stranded post-pass can still clear its spinners — WITHOUT settling a
  // run that merely had its first agent final (generative fan-out shares one turn; L19).
  const summaryFinaledRunIds = new Set();
  // releaseVersion (topology tag) of the CURRENT run + a per-run map {runId → releaseVersion}.
  // Stamped from the run's EXECUTION_STARTED / AGENT_FINAL_RESPONSE extras (backend). Lets the room
  // fetch THAT run's manifest — an old run keeps its old topology after a MAJOR upgrade. Null when
  // unpinned/live (room falls back to its current manifest).
  let currentRunReleaseVersion = null;
  const runReleaseVersions = {};

  // Track which optimistic entries the backend has confirmed so we can drop
  // them from the final feed (the backend `USER_PROMPT` becomes the truth).
  const reconciledOptimisticIds = new Set();
  const optimisticByCorrelation = new Map();
  if (Array.isArray(opts.optimistic)) {
    for (const o of opts.optimistic) {
      if (o && o.correlationId) optimisticByCorrelation.set(o.correlationId, o);
    }
  }

  /** Enforce unique ``turn.id`` (React list keys). Reused execution names and
   *  missing per-envelope ``eventId`` can otherwise collide when a new run
   *  opens after the previous one with the same SFN name (history + live,
   *  or a completed turn followed by a fresh EXECUTION_* replay). */
  function disambiguateTurnId(preferred) {
    const base = String(preferred || `turn-${turnGroups.length}`);
    for (let n = 0; n < 2000; n += 1) {
      const candidate = n === 0 ? base : `${base}#${n}`;
      if (!turnGroups.some((t) => t.id === candidate)) return candidate;
    }
    return `${base}#t${Date.now()}`;
  }

  /** Open a new turn group, closing the previous live one if still open. */
  function openTurn({ id, correlationId, userEntry, startedAt }) {
    if (liveTurn && liveTurn.status === 'live') {
      // The previous turn never received an AGENT_FINAL_RESPONSE — close
      // it gracefully so its thinking transcript isn't bled into the new
      // one. This happens on supervisor approvals that immediately spawn a
      // follow-up run, and on reconnect-after-completion.
      liveTurn.status = 'completed';
      liveTurn.activeAgentId = undefined;
    }
    const turnId = disambiguateTurnId(id);
    const turn = newTurn({ id: turnId, correlationId, userEntry, startedAt });
    // Tag with the current top-level run so the room can fold the whole run into one tree.
    turn.runId = currentRunId || undefined;
    turnGroups.push(turn);
    liveTurn = turn;
    return turn;
  }

  /** Lazy-create a live turn when activity arrives without an explicit user
   *  prompt (eg. EXECUTION_STARTED replays from REST history). */
  function ensureLiveTurn(env) {
    if (liveTurn && liveTurn.status === 'live') return liveTurn;
    return openTurn({
      id: String(env.correlationId || env.eventId || `turn-${turnGroups.length}`),
      correlationId: typeof env.correlationId === 'string' ? env.correlationId : undefined,
      userEntry: null,
      startedAt: tsOf(env),
    });
  }

  /** RESILIENCE — form the run block WITHOUT the EXECUTION_STARTED beacon (ROOMS.md §run-block-formation).
   *  ``currentRunId`` is the key the room folds a run's turns into ONE hierarchy tree (the "network
   *  block": agent tree + Network Summarizer). It is normally set ONLY by the top-level
   *  EXECUTION_STARTED case below — but that beacon is published by the SFN→AppSync bridge, and if
   *  EventBridge drops it (a delivery miss) currentRunId stays null → the block never forms, the
   *  agent/synthesizer streams have nowhere to render, and the Summarizer row sticks at ``standby`` —
   *  LIVE and on history rehydrate (the beacon isn't in the persisted feed either). So we ALSO adopt
   *  currentRunId from events the engine publishes DIRECTLY (reliably delivered): the top-level
   *  USER_PROMPT and the first agent event. Their ``correlationId`` == the top-level run id (the room
   *  publisher stamps ``correlation_id = execution_id`` on every event, children included), so it
   *  matches exactly what EXECUTION_STARTED would have set. EXECUTION_STARTED still WINS when present
   *  (it resets the roster). Guarded on ``!currentRunId`` so a later run is never re-folded into an
   *  earlier one. See test_room_block_forms_without_execution_started + LESSONS L3. */
  function adoptRunId(candidate) {
    const c = String(candidate || '').trim();
    if (!c) return;
    // KICK-SEEDED authoritative boundary: the room told us (opts.pendingRunId) the id of the run it
    // just kicked. An event carrying THAT id is definitively the new run — so force its block even
    // when ``currentRunId`` still points at the prior run (the ``!currentRunId`` guard below would
    // otherwise fold run N+1 into run N when the run-start beacon is delayed/reordered, which is what
    // left the roster on standby after an artifact-delayed kick). Mirrors ``beginRunFromPrompt``'s
    // reset; only the FIRST event force-resets (``c !== currentRunId``), the rest are no-ops.
    if (c === pendingRunId && c !== currentRunId) {
      currentRunId = c;
      roster = {};
      pendingBuilderWaits.clear();
      rostersByRun[currentRunId] = roster;
      if (liveTurn && !liveTurn.runId) liveTurn.runId = currentRunId;
      return;
    }
    if (currentRunId) return;
    currentRunId = c;
    // Bind the live working roster to this run (mirror the EXECUTION_STARTED path) so the tree folds
    // and history keeps it; re-stamp any turn opened before we knew the run id.
    if (!rostersByRun[currentRunId]) rostersByRun[currentRunId] = roster;
    if (liveTurn && !liveTurn.runId) liveTurn.runId = currentRunId;
  }

  /** A ``USER_PROMPT`` is ALWAYS a top-level run boundary (see ``isRunStartEnvelope``) — so it must
   *  START a new run block, NOT merely fill the first one. ``adoptRunId``'s ``!currentRunId`` guard
   *  only formed the FIRST run's block; run N+1 then FOLDED into run N whenever the bridge dropped its
   *  EXECUTION_STARTED (the cheeky-lungfish/poppy incident: "Plan ARCHITECTURE" folded into the "Plan
   *  SCOPING" block, showing the stale scoping summary). Because EXECUTION_STARTED is unreliable
   *  (bridge SPOF), the prompt is the authoritative boundary: on a NEW correlationId, advance
   *  currentRunId + reset the per-run roster exactly like the top-level EXECUTION_STARTED path. If
   *  EXECUTION_STARTED later arrives with the same id it's a harmless no-op reset; a prompt whose corr
   *  equals the current run (its own late/echoed prompt) attaches without resetting. */
  function beginRunFromPrompt(corr) {
    const c = String(corr || '').trim();
    if (!c) return;               // no correlationId to key on → leave run framing to other events
    if (c === currentRunId) return;   // this run's own prompt → attach, don't reset
    currentRunId = c;
    roster = {};
    pendingBuilderWaits.clear();
    rostersByRun[currentRunId] = roster;
  }

  /**
   * After ``AGENT_FINAL_RESPONSE`` the turn is no longer *live*; the next
   * ``LOG_BATCH`` would otherwise call ``ensureLiveTurn`` and open a **new**
   * group — making a duplicate "Thinking" block render **below** the answer.
   * Walk backward and merge orphan log lines into the most recent group that
   * matches this envelope's ``executionId`` / ``correlationId``.
   */
  function executionHintFromLogPayload(p) {
    if (!p || typeof p !== 'object') return '';
    const top = String(p.executionId || p.execution_id || '').trim();
    if (top) return top;
    const logs = Array.isArray(p.logs) ? p.logs : [];
    for (let u = 0; u < logs.length; u += 1) {
      const row = logs[u];
      if (row && typeof row === 'object') {
        const t = String(
          row.execution_id || row.executionId || row.taskId || row.task_id || '',
        ).trim();
        if (t) return t;
      }
    }
    return '';
  }

  function turnForLogAppend(env) {
    if (liveTurn && liveTurn.status === 'live') return liveTurn;
    const p = payloadOf(env);
    const eid = String(
      p.executionId
      || env?.executionId
      || p.execution_id
      || executionHintFromLogPayload(p)
      || '',
    ).trim();
    const cid = typeof env.correlationId === 'string' ? env.correlationId.trim() : '';
    for (let idx = turnGroups.length - 1; idx >= 0; idx -= 1) {
      const g = turnGroups[idx];
      if (eid) {
        if (g.executionId && g.executionId === eid) return g;
        if (g.correlationId && g.correlationId === eid) return g;
        if (g.id) {
          const idStr = String(g.id);
          if (idStr === eid) return g;
          const hash = idStr.indexOf('#');
          if (hash > 0 && idStr.slice(0, hash) === eid) return g;
        }
      }
      if (cid) {
        if (g.correlationId && g.correlationId === cid) return g;
        if (g.executionId && g.executionId === cid) return g;
      }
    }
    return ensureLiveTurn(env);
  }

  /** Force-close EVERY still-live turn, stamping ``endedAt`` so its elapsed timer stops.
   *  Called on any TOP-LEVEL run terminal signal (EXECUTION_COMPLETED/FAILED, or a terminal
   *  UI_STATE). The generative engine fans out into MANY turns (one per dispatched agent), and
   *  the single-``liveTurn`` close missed stragglers — a turn left ``live`` ticks its header
   *  clock forever. The backend bridge only emits top-level EXECUTION_* (sub-executions +
   *  map children are skipped), so when we see one the whole run really is done → safe to
   *  close all. Idempotent: a turn already completed keeps its original ``endedAt``. */
  function closeAllLiveTurns(endTs) {
    for (const g of turnGroups) {
      if (g && g.status === 'live') {
        g.status = 'completed';
        g.endedAt = g.endedAt || endTs;
        g.activeAgentId = undefined;
      }
    }
    // Any roster row still marked running/awaiting when the whole run ends is stranded —
    // settle it to done so the far-left spinner stops (mirrors the header-timer close).
    // EXCEPT the async Planner: it runs OUTSIDE the state function (fire-and-forget lambda), so the
    // SFN's EXECUTION_COMPLETED is NOT its completion — settling it here flipped it to "done" while
    // the Tasks-tab beacon was still waiting for PLAN_UPDATED (the user-reported "early done"). It
    // settles ONLY on its own terminal PLAN_UPDATED.
    for (const id of Object.keys(roster)) {
      const row = roster[id];
      if (row && !row.isPlanner && (row.status === 'running' || row.status === 'awaiting')) {
        row.status = 'done';
      }
    }
    // The run is over → no builder can still be awaiting the user (a live wait would have kept the
    // run non-terminal). Drop any stragglers so the room's "N awaiting" notice clears on completion.
    pendingBuilderWaits.clear();
  }

  const _TERMINAL_UI_STATES = new Set([UI_STATES.IDLE, UI_STATES.COMPLETED, UI_STATES.FAILED]);

  for (let i = 0; i < envelopes.length; i += 1) {
    const env = envelopes[i];
    if (!env || typeof env !== 'object') continue;
    const et = envelopeEventType(env);
    if (!et) continue;
    const p = payloadOf(env);
    const corr = typeof env.correlationId === 'string' ? env.correlationId : '';

    switch (et) {
      case AGENT_EVENTS.UI_STATE: {
        // SUB-EXECUTION GUARD (frank-beaver done-early, LESSONS L18): the bridge publishes a room
        // UI_STATE for each worker/builder ``.sync`` child too, stamped with the CHILD's executionId
        // and NO stateMachineArn. The first child finishing therefore published UI_STATE=IDLE ~6 min
        // before the run's real terminal — flipping the room ``done`` mid-run AND (because the RUNSTATE
        // reconcile is gated on uiState===RUNNING) freezing the tree out of sync with the snapshot.
        // A child's UI_STATE is NEVER the room's lifecycle — ignore it. (Backend also fixed to stop
        // emitting these; this is the belt-and-suspenders REALTIME-HUB never-trust-a-non-root-lifecycle.)
        const _uiExecId = String(p.executionId || env.executionId || env.correlationId || '').trim();
        if (_uiExecId && subExecIds.has(_uiExecId)) break;
        const u = typeof p.uiState === 'string' ? p.uiState.toUpperCase().trim() : '';
        if (u) uiState = u;
        // RUNNING transitions clear any stale approval state from a prior
        // run; APPROVAL_RESOLVED is the canonical clear, but a fresh
        // UI_STATE: RUNNING covers reconnect cases where we missed it.
        if (u === UI_STATES.RUNNING) pendingApproval = null;
        // A terminal room state ends the run → stop every live turn's header timer.
        if (_TERMINAL_UI_STATES.has(u)) closeAllLiveTurns(tsOf(env));
        break;
      }
      case AGENT_EVENTS.EXECUTION_STARTED: {
        lastExecution = {
          executionId: String(p.executionId || env.executionId || ''),
          status: 'RUNNING',
          startedAt: tsOf(env),
        };
        // ROSTER RESET GUARD — the SFN→AppSync bridge publishes EXECUTION_STARTED for EVERY .sync
        // sub-execution (each generative worker/builder child), NOT just the top-level run. So we
        // must reset the per-run roster + advance currentRunId ONLY for the TOP-LEVEL run — else a
        // later layer's worker children wipe the earlier layers' roster mid-run (the layer-1 leads
        // then fall out of the tree and render as flat floating cards). Discriminate on the ARN:
        // sub-executions are ``universal-worker``/``universal-builder``; the top-level run is the
        // engine (or, for classic ASL networks, the network's own runtime SFN). Verified against a
        // real GOOG run — see test_execution_started_subexec_does_not_reset_roster.
        if (isSubExecutionStarted(p, env)) {
          // A generative worker/builder .sync CHILD started — NOT a new run. Do not reset the
          // roster, advance currentRunId, or open/fragment a turn. Its AGENT_FINAL_RESPONSE will
          // attach to the live top-level turn (ensureLiveTurn) and populate the run's roster.
          // Remember its id so a later UI_STATE stamped with it (which carries no ARN to filter on)
          // is ignored — a child finishing must never flip the room's RUNNING/IDLE.
          const childId = String(p.executionId || env.executionId || '').trim();
          if (childId) subExecIds.add(childId);
          break;
        }
        roster = {};
        pendingBuilderWaits.clear();
        currentRunId = String(p.executionId || env.executionId || corr || '').trim() || currentRunId;
        // Preserve THIS run's roster under its runId so history keeps every run's tree. ``roster``
        // is the live working map for the current run; storing the SAME reference means subsequent
        // upserts to ``roster`` flow through to rostersByRun[currentRunId] automatically.
        if (currentRunId) rostersByRun[currentRunId] = roster;
        // Tag this run with the topology version it executed on (backend stamps it on the payload).
        {
          const rv = Number(p.releaseVersion);
          if (Number.isFinite(rv) && rv >= 1) {
            currentRunReleaseVersion = rv;
            if (currentRunId) runReleaseVersions[currentRunId] = rv;
          }
        }
        // Start a fresh turn if none is open yet for this correlation. Any
        // prior open turn (eg. interrupted) is closed by openTurn(). When
        // a USER_PROMPT later lands with the same correlationId we'll
        // attach it to the live turn instead of opening a second.
        if (!liveTurn || liveTurn.status !== 'live'
          || (liveTurn.correlationId && corr && liveTurn.correlationId !== corr)) {
          openTurn({
            id: String(env.eventId || corr || `turn-${turnGroups.length}`),
            correlationId: corr || undefined,
            userEntry: null,
            startedAt: tsOf(env),
          });
        } else {
          // Keeping a turn that USER_PROMPT opened before this EXECUTION_STARTED — re-stamp it with
          // the now-known run id so it folds into the current run's tree (it was created under the
          // prior run's currentRunId).
          liveTurn.runId = currentRunId || liveTurn.runId;
        }
        break;
      }
      case AGENT_EVENTS.EXECUTION_COMPLETED: {
        // RUN-TERMINAL GUARD (same reason as EXECUTION_STARTED): the bridge emits
        // EXECUTION_COMPLETED for every .sync sub-execution too. Treat only the TOP-LEVEL run's
        // completion as run-terminal — otherwise the FIRST worker/builder child to finish would
        // prematurely close the live turn, settle the whole roster to done (spinners stop while
        // peers still run), and clear pending builder waits mid-run. The builder-completion
        // synthesis below is intentionally OUTSIDE this guard — it legitimately keys off a
        // universal-builder CHILD's completion to build its log card.
        const isSubC = isSubExecutionStarted(p, env);
        if (!isSubC) {
          lastExecution = {
            executionId: String(p.executionId || env.executionId || ''),
            status: 'COMPLETED',
            startedAt: lastExecution?.startedAt,
            endedAt: tsOf(env),
          };
          if (liveTurn && liveTurn.status === 'live') {
            liveTurn.status = 'completed';
            liveTurn.endedAt = tsOf(env);
            liveTurn.activeAgentId = undefined;
          }
          // Top-level run done → close ANY straggler live turns + settle the roster.
          closeAllLiveTurns(tsOf(env));
        }
        // Synthesize builderCompletion for universal-builder successes.
        // Derive S3 keys from the turn's correlationId (= parent runId) + orgTenantId.
        // LEGACY FALLBACK ONLY: skip when per-agent completions already exist
        // (new executions carry authoritative per-agent keys via BUILDER_PHASE).
        const sfnArnC = typeof p.stateMachineArn === 'string' ? p.stateMachineArn : '';
        if (sfnArnC.includes('universal-builder')) {
          const turn = liveTurn || (turnGroups.length > 0 ? turnGroups[turnGroups.length - 1] : null);
          if (turn && !turn.builderCompletion
              && (!Array.isArray(turn.builderCompletions) || turn.builderCompletions.length === 0)) {
            const runId = turn.correlationId || '';
            const tenantId = typeof env.orgTenantId === 'string' ? env.orgTenantId : '';
            const prefix = (tenantId && runId) ? `runs/${tenantId}/${runId}/` : '';
            turn.builderCompletion = {
              exitCode: 0,
              mode: 'background',
              logKey: prefix ? `${prefix}builder_output.txt` : '',
              eventsKey: prefix ? `${prefix}builder_events.json` : '',
            };
          }
        }
        break;
      }
      case AGENT_EVENTS.EXECUTION_FAILED: {
        // RUN-TERMINAL GUARD — a failing .sync CHILD is a per-agent failure the engine's node
        // reducer routes around (retry / accept-best / descend); it must NOT fail the whole run in
        // the UI. Only the TOP-LEVEL run's EXECUTION_FAILED is run-terminal. (In the real GOOG run,
        // several worker children emitted EXECUTION_FAILED mid-run while the run continued fine.)
        const isSubF = isSubExecutionStarted(p, env);
        if (!isSubF) {
          lastExecution = {
            executionId: String(p.executionId || env.executionId || ''),
            status: 'FAILED',
            startedAt: lastExecution?.startedAt,
            endedAt: tsOf(env),
          };
          // Mark the run's turn FAILED — even if it already read ``completed``. A worker .sync
          // child's EXECUTION_COMPLETED (or its UI_STATE=IDLE) can flip the live turn to
          // ``completed`` SECONDS before the top-level engine EXECUTION_FAILED arrives (verified
          // on the wire: last worker IDLE at 15:37:24.4, engine FAILED at 15:37:24.9). The old
          // ``status === 'live'`` guard then skipped the failure, leaving the turn ``completed``
          // with no Summary → the room spun "Network running" forever. A top-level failure is
          // authoritative: it OVERRIDES a stale ``completed`` (but not a real terminal we set here).
          const _failTurn = (liveTurn && (liveTurn.status === 'live' || liveTurn.status === 'completed'))
            ? liveTurn
            : turnGroups.find((g) => g && g.status !== 'failed'
                && (g.executionId === lastExecution.executionId
                    || g.runId === lastExecution.executionId
                    || g.correlationId === lastExecution.executionId));
          if (_failTurn) {
            _failTurn.status = 'failed';
            _failTurn.endedAt = tsOf(env);
            _failTurn.activeAgentId = undefined;
            // Capture the run-level failure REASON so the UI can show WHY (not a silent fail). The
            // bridge enriches ``cause`` with the real per-agent reason (e.g. the $2 max-spend cap)
            // when the top-level SFN error was the opaque ExceedToleratedFailureThreshold.
            const _reason = (typeof p.cause === 'string' && p.cause.trim())
              || (typeof p.error === 'string' && p.error.trim())
              || '';
            if (_reason) _failTurn.error = _reason;
          }
          // Top-level run failed → stop every straggler live turn's timer too.
          closeAllLiveTurns(tsOf(env));
        }
        // Synthesize builderCompletion for universal-builder failures so
        // BuilderLogCard renders on reconnect even without BUILDER_PHASE
        const sfnArn = typeof p.stateMachineArn === 'string' ? p.stateMachineArn : '';
        if (sfnArn.includes('universal-builder')) {
          const turn = liveTurn || (turnGroups.length > 0 ? turnGroups[turnGroups.length - 1] : null);
          if (turn && !turn.builderCompletion
              && (!Array.isArray(turn.builderCompletions) || turn.builderCompletions.length === 0)) {
            const runIdF = turn.correlationId || '';
            const tenantIdF = typeof env.orgTenantId === 'string' ? env.orgTenantId : '';
            const prefixF = (tenantIdF && runIdF) ? `runs/${tenantIdF}/${runIdF}/` : '';
            turn.builderCompletion = {
              exitCode: 1,
              mode: 'unknown',
              logKey: prefixF ? `${prefixF}builder_output.txt` : '',
              eventsKey: prefixF ? `${prefixF}builder_events.json` : '',
              error: typeof p.cause === 'string' ? p.cause : typeof p.error === 'string' ? p.error : 'Build failed',
            };
          }
        }
        break;
      }
      case AGENT_EVENTS.USER_PROMPT: {
        // Bridge-miss resilience: a prompt is a run BOUNDARY — start (or advance to) its own block even
        // if EXECUTION_STARTED never lands, so run N+1 never folds into run N (correlationId == run id).
        beginRunFromPrompt(corr);
        const text = typeof p.text === 'string' ? p.text : typeof p.prompt === 'string' ? p.prompt : '';
        if (corr && optimisticByCorrelation.has(corr)) {
          reconciledOptimisticIds.add(optimisticByCorrelation.get(corr).id);
        }
        const entry = {
          kind: 'user',
          id: String(env.eventId || `prompt-${i}`),
          eventId: env.eventId,
          correlationId: corr || undefined,
          actorName: typeof p.actorName === 'string' ? p.actorName : undefined,
          text,
          ts: tsOf(env),
        };
        feed.push(entry);
        // Attach to an already-open turn (EXECUTION_STARTED arrived first)
        // when correlationIds line up; otherwise open a fresh turn.
        if (liveTurn && liveTurn.status === 'live'
          && (!liveTurn.correlationId || !corr || liveTurn.correlationId === corr)
          && !liveTurn.userEntry) {
          liveTurn.userEntry = entry;
          if (corr && !liveTurn.correlationId) liveTurn.correlationId = corr;
        } else {
          openTurn({
            id: entry.id,
            correlationId: corr || undefined,
            userEntry: entry,
            startedAt: entry.ts,
          });
        }
        break;
      }
      case AGENT_EVENTS.AGENT_STARTED: {
        // Per-agent KICKOFF beat: mark this agent's roster row ``running`` (spinner + blue graph
        // tint) the instant it begins — BEFORE its AGENT_FINAL_RESPONSE. Without this the seeded
        // ``standby`` row jumps straight to ``done`` (no transition). The worker stamps agentName =
        // its agentId plus the tree position (nodePath/parentAgentId/level), same as the final. The
        // upsert is monotonic, so a late/replayed START can't regress an already-done row.
        // Bridge-miss resilience: the first agent kickoff also seeds the run block (corr == run id).
        adoptRunId(corr);
        const rosterId = String(p.agentName || p.agentId || '').trim();
        if (rosterId && rosterId !== 'Agent') {
          const turn = ensureLiveTurn(env);
          setActiveAgent(turn, rosterId);
          const lvl = Number(p.level);
          // ENGINE-STAGE row (Lead router / Node-Reducer judge): a synthetic tree line for the real
          // LLM work the engine does between agents — so the tree isn't frozen while it routes/judges.
          // Flagged ``isEngineStage`` + ``stageKind`` so the FE renders a spinner + "Working…" and the
          // reader titles it "Lead Reasoning" / "Reasoning Summarizer". The Final synthesizer reuses
          // the real "Summary" row (agentName=Summary) so it is NOT flagged a stage — it just gets a
          // running→done transition, giving the Summary block its live Working state.
          const _isStage = p.isEngineStage === true && p.agentName !== 'Summary';
          // ENGINE-STAGE placement: derive the clean label ("Network Lead"/"Lead"/"Summarizer") +
          // nesting (under the owning node) from nodePath — the SAME helper the finalize path uses,
          // so live and history render identically. For a normal agent, keep the wire's label/level.
          const _sp = _isStage
            ? engineStagePlacement(typeof p.stage === 'string' ? p.stage : '',
                                   typeof p.nodePath === 'string' ? p.nodePath : 'root')
            : null;
          upsertAgentRow(roster, rosterId, {
            label: _sp ? _sp.label : ((typeof p.label === 'string' && p.label) ? p.label : rosterId),
            status: 'running',
            level: _sp ? _sp.level : (Number.isFinite(lvl) && lvl > 0 ? lvl : undefined),
            parentAgentId: _sp ? _sp.parentAgentId
              : ((typeof p.parentAgentId === 'string' && p.parentAgentId) ? p.parentAgentId : undefined),
            nodePath: (typeof p.nodePath === 'string' && p.nodePath) ? p.nodePath : undefined,
            isBuilder: p.isBuilder === true ? true : undefined,
            isEngineStage: _isStage ? true : undefined,
            stageKind: _isStage ? (typeof p.stage === 'string' ? p.stage : '') : undefined,
          });
        }
        break;
      }
      case AGENT_EVENTS.AGENT_FINAL_RESPONSE: {
        // Bridge-miss resilience: even a run that ONLY yields a final (incl. the Summary synthesis)
        // must form its block so the answer renders. corr == the top-level run id. No-op once known.
        adoptRunId(corr);
        const entry = {
          kind: 'agent',
          id: String(env.eventId || `agent-${i}`),
          eventId: env.eventId,
          agentName: typeof p.agentName === 'string' ? p.agentName : 'Agent',
          text: typeof p.text === 'string' ? p.text : '',
          ts: tsOf(env),
          // Per-turn Bedrock usage chip (model + tokens + cost). Worker
          // emits this on AGENT_FINAL_RESPONSE; ``undefined`` for agents
          // that never called Bedrock (e.g. tool-only turns).
          usage: (p && typeof p.usage === 'object' && p.usage) ? p.usage : undefined,
        };
        feed.push(entry);
        const turn = ensureLiveTurn(env);
        const eidA = String(p.executionId || env.executionId || corr || '').trim();
        if (eidA) {
          turn.executionId = eidA;
        }
        // Topology tag fallback: if EXECUTION_STARTED didn't carry releaseVersion (e.g. missed on
        // reconnect), a worker final does — tag the run so per-run topology still resolves.
        {
          const rvf = Number(p.releaseVersion);
          if (Number.isFinite(rvf) && rvf >= 1) {
            const rid = turn.runId || currentRunId;
            if (rid && !runReleaseVersions[rid]) runReleaseVersions[rid] = rvf;
            if (rid && rid === currentRunId && currentRunReleaseVersion == null) {
              currentRunReleaseVersion = rvf;
            }
          }
        }
        turn.agentEntries.push(entry);
        // ROSTER: place this agent in the live tree (done) with its hierarchy. The worker stamps
        // agentId as ``agentName`` and the tree position (nodePath/parentAgentId/level) at the
        // payload top level (see events.publish_agent_final_response ``extra``). Absent for
        // non-generative agents → level defaults to 1, parent null (flat).
        {
          // AVATAR CONTRACT: map through the SAME identity key the avatar's phases/stream use, so its
          // Output lands on its own row (f.synth's "Network Summarizer" → __reducer__root) instead of
          // spawning a second one. Builders/workers map to themselves (no-op).
          const rosterId = reducerRosterKey(String(p.agentName || '').trim());
          if (rosterId && rosterId !== 'Agent') {
            const lvl = Number(p.level);
            upsertAgentRow(roster, rosterId, {
              label: rosterId,
              status: 'done',
              level: Number.isFinite(lvl) && lvl > 0 ? lvl : undefined,
              parentAgentId: (typeof p.parentAgentId === 'string' && p.parentAgentId)
                ? p.parentAgentId : undefined,
              nodePath: (typeof p.nodePath === 'string' && p.nodePath) ? p.nodePath : undefined,
              // Set usage ONLY when this event carries it. A BUILDER agent's cost arrives on its
              // BUILDER_PHASE_COMPLETED (summary.usage); the engine then emits a second, usage-less
              // AGENT_FINAL_RESPONSE (the claim-check) for the SAME row — writing ``usage: undefined``
              // here clobbered the builder's cost → the run total dropped it ($0). Preserve the prior
              // usage when the event has none (workers/lead/reducer still set theirs, which is present).
              ...(entry.usage ? { usage: entry.usage } : {}),
              text: entry.text,
            });
          }
        }
        // FINAL synthesizer ('Summary') landing = the run is genuinely done (it is the engine's last
        // node). Record it so the settle post-pass may clear stragglers even if the terminal beacon
        // was dropped — but a NON-Summary agent final must NOT (siblings may still be running, L19).
        if (String(p.agentName || '').trim() === 'Summary') {
          summaryFinaledRunIds.add(String(turn.runId || currentRunId || corr || '').trim());
        }
        turn.status = 'completed';
        turn.endedAt = tsOf(env);
        // Final response → the previously-active agent has now had its
        // turn. Mark it completed so MiniFlow keeps it lit (not pulsing)
        // once the run wraps up.
        if (turn.activeAgentId) {
          if (!(turn.completedAgentIds instanceof Set)) {
            turn.completedAgentIds = new Set();
          }
          turn.completedAgentIds.add(turn.activeAgentId);
        }
        turn.activeAgentId = undefined;
        break;
      }
      case AGENT_EVENTS.JUDGE_VERDICT: {
        // Objective judge (SCHEDULER.md §11.1b) — render as a distinct "Judge"
        // voice in the feed so the loop's reasoning is visible (no black box).
        const entry = {
          kind: 'agent',
          id: String(env.eventId || `judge-${i}`),
          eventId: env.eventId,
          agentName: 'Judge',
          isJudge: true,
          rating: (typeof p.rating === 'number') ? p.rating : null,
          confidence: (typeof p.confidence === 'number') ? p.confidence : null,
          improvements: typeof p.improvements === 'string' ? p.improvements : '',
          iteration: (typeof p.iteration === 'number') ? p.iteration : undefined,
          text: typeof p.text === 'string' ? p.text : '',
          ts: tsOf(env),
        };
        feed.push(entry);
        break;
      }
      case AGENT_EVENTS.APPROVAL_REQUESTED: {
        if (typeof p.taskToken === 'string' && p.taskToken.length > 0) {
          pendingApproval = {
            taskToken: p.taskToken,
            taskId: typeof p.taskId === 'string' ? p.taskId : String(env.correlationId || ''),
            agentName: typeof p.agentName === 'string' ? p.agentName : undefined,
            design: designOf(p.design),
            eventId: env.eventId,
          };
        }
        break;
      }
      case AGENT_EVENTS.APPROVAL_RESOLVED: {
        // Resolve only if it matches the currently-tracked approval; a
        // resolution for an older lease shouldn't dismiss a fresh one.
        if (pendingApproval && (
          pendingApproval.taskId === (typeof p.taskId === 'string' ? p.taskId : '')
          || pendingApproval.taskId === String(env.correlationId || '')
        )) {
          pendingApproval = null;
        }
        break;
      }
      case AGENT_EVENTS.LOG_LINE: {
        const streamId = typeof p.stream === 'string' ? p.stream.trim() : '';
        if (streamId === 'universal_builder') {
          const turn = turnForLogAppend(env);
          if (!Array.isArray(turn.builderTerminalLines)) turn.builderTerminalLines = [];
          const bl = turn.builderTerminalLines;
          const MAX_UB = 5000;
          // Normalize to a cc-line object (structured ccEvent, else legacy plain text).
          let ccLine = null;
          if (p.ccEvent && typeof p.ccEvent === 'object') {
            ccLine = p.ccEvent;
          } else {
            const lineText = typeof p.text === 'string' ? p.text : '';
            if (lineText.length > 0) ccLine = { cc: 'text', text: lineText };
          }
          if (ccLine) {
            // LANE-B tail dedup (REALTIME-HUB §0.1): each builder LOG_LINE carries a monotonic
            // ``lineNo``. A mid-build join backfills the tail (replayed through this same case), so a
            // live line whose lineNo we've already applied (from the tail, or a duplicate) is DROPPED —
            // the tail and the live stream merge idempotently, no double-printed lines at the seam.
            const bDisplay = typeof p.agentId === 'string' ? p.agentId.trim() : '';
            const bAgent = reducerRosterKey(bDisplay);   // f.synth merge: land on __reducer__root
            const lineNo = Number(p.lineNo);
            const hasNo = Number.isFinite(lineNo) && lineNo > 0;
            if (bAgent && hasNo) {
              const hw = Number(roster[bAgent] && roster[bAgent].builderMaxLineNo) || 0;
              if (lineNo <= hw) break;   // already applied (tail backfill or dup) — skip
            }
            // Flat turn stream (back-compat + flat/history render).
            if (bl.length < MAX_UB) bl.push(ccLine);
            // PER-AGENT bucket on the run-level roster row → the single shared container shows
            // THIS builder's live terminal when its row is selected (the tree UX). agentId is
            // stamped on the builder cc-event payload (stream_runner). Absent → flat-only.
            if (bAgent) {
              const prev = roster[bAgent] || {};
              const lines = Array.isArray(prev.builderLines) ? prev.builderLines : [];
              if (lines.length < MAX_UB) lines.push(ccLine);
              // Mark it a running builder so the container picks the CLI renderer (row APPEARS
              // here if its BUILDER_PHASE hasn't landed yet). Monotonic guard protects done/failed.
              // Label = the DISPLAY id (a mapped reducer row keeps its pretty name, not the key).
              const patch = {
                label: bDisplay, isBuilder: true, status: 'running', builderLines: lines,
              };
              if (hasNo) patch.builderMaxLineNo = Math.max(prev.builderMaxLineNo || 0, lineNo);
              upsertAgentRow(roster, bAgent, patch);
            }
          }
          break;
        }
        const singletonLines = envelopeToThinkingLines(env);
        if (!singletonLines.length) break;
        const turnSb = turnForLogAppend(env);
        turnSb.thinking.push(...singletonLines);
        for (let k = singletonLines.length - 1; k >= 0; k -= 1) {
          if (singletonLines[k].agentId) {
            setActiveAgent(turnSb, singletonLines[k].agentId);
            // ROSTER: an agent producing activity is running. Row APPEARS here if it hasn't
            // finalized yet (a lead streams tool calls long before its final). Monotonic guard
            // keeps a done/awaiting row from regressing; hierarchy fills in on the final.
            upsertAgentRow(roster, singletonLines[k].agentId,
              { label: singletonLines[k].agentId, status: 'running' });
            break;
          }
        }
        break;
      }
      case AGENT_EVENTS.BUILDER_PHASE: {
        const turn = turnForLogAppend(env);
        if (!Array.isArray(turn.builderPhases)) turn.builderPhases = [];
        // Use the envelope's wire timestamp (chronological) rather than
        // ``Date.now()``, otherwise on REST replay every phase appears
        // to land at "right now" — which breaks chronological agent
        // ordering inside a turn (the Builder surface gets timestamped
        // after every other agent's final and renders in the wrong
        // slot in the side-panel detail view).
        const phaseTs = tsOf(env) || Date.now();
        const phase = {
          phase: typeof p.phase === 'string' ? p.phase : '',
          status: typeof p.status === 'string' ? p.status : '',
          label: typeof p.label === 'string' ? p.label : '',
          ts: phaseTs,
        };
        turn.builderPhases.push(phase);
        // Capture the builder's OWN runId (payload.runId == the runner's
        // execution-name / blood.meta.taskId). This is the id the builder keys
        // its cancel row + kickoff on — and it is NOT the same as the turn's
        // ``executionId`` (which can be a runtime UUID). The Stop button MUST
        // send THIS id, or the cancel flag lands under the wrong PK and the
        // builder's poller never sees it (Stop silently no-ops). See the
        // stop-diag incident: poller watched RUN#api-<net>-<ts> while the cancel
        // API wrote RUN#<uuid>. Bind Stop to builderRunId end-to-end.
        if (typeof p.runId === 'string' && p.runId.trim()) {
          turn.builderRunId = p.runId.trim();
        }
        // ROSTER: mark the phase's agent as a RUNNING BUILDER (with its runId) on any non-terminal
        // phase. This is what surfaces the per-row STOP control — ``runningBuilderRunIds`` requires
        // the roster row to be ``isBuilder && status==='running' && runId``. A Lead-FLEXED builder's
        // seeded row is ``isBuilder:false`` (its manifest type is AI/worker), so without this the
        // builder runs with NO Stop button (the reported gap). Terminal phases (COMPLETED/FAILED)
        // are handled below (→ done/failed); guard on that so we don't re-open a finished builder.
        {
          const _bpDisplay = (typeof p.agentId === 'string' && p.agentId.trim()) ? p.agentId.trim() : '';
          const _bpAgent = reducerRosterKey(_bpDisplay);   // f.synth merge: land on __reducer__root
          const _bpRun = (typeof p.runId === 'string' && p.runId.trim()) ? p.runId.trim() : '';
          const _terminalPhase = phase.phase === 'COMPLETED' || phase.status === 'completed'
            || phase.status === 'failed';
          if (_bpAgent && !_terminalPhase) {
            upsertAgentRow(roster, _bpAgent, {
              label: _bpDisplay, isBuilder: true, status: 'running',
              runId: _bpRun || undefined,
            });
          }
        }
        // Stamp the *first* builder phase ts on the turn so the
        // renderer can place the Builder surface at the right
        // chronological slot relative to other agents' final
        // responses. (Without this hint the renderer can only sort
        // by per-phase ts which is fine, but cheaper to pin once.)
        if (turn.builderStartedAt == null) {
          turn.builderStartedAt = phaseTs;
        }
        if (phase.phase === 'COMPLETED' && p.summary) {
          const s = p.summary;
          // Per-agent discriminator: prefer the authoritative summary.agentId,
          // fall back to the envelope-level payload.agentId. When present, the
          // room renders one card per agent (each with its own logKey/eventsKey
          // straight off the summary — no S3-prefix reconstruction). When
          // absent (legacy executions), we keep the single-completion behavior.
          const agentId = (typeof s.agentId === 'string' && s.agentId)
            || (typeof p.agentId === 'string' && p.agentId)
            || '';
          if (!Array.isArray(turn.builderCompletions)) turn.builderCompletions = [];
          if (agentId) {
            const rec = { ...s, agentId, ts: phaseTs };
            const ix = turn.builderCompletions.findIndex((c) => c.agentId === agentId);
            if (ix >= 0) turn.builderCompletions[ix] = rec; // idempotent on replay
            else turn.builderCompletions.push(rec);
          }
          // Keep the legacy single field last-wins for old consumers + the
          // no-agentId fallback path.
          turn.builderCompletion = s;
          // ROSTER: the builder finished → done. Also clear any lingering pending wait for its
          // runId (a builder that completes without an explicit RESOLVED shouldn't stay "awaiting").
          if (agentId) {
            // DEGRADED (governed stop: policy cap / timeout) is a DONE-family outcome — the builder
            // finished under a limit with usable partial work. NOT a failure: the row shows an amber
            // degraded state + reason, and the run is not marked failed. ``s.degraded`` rides the
            // COMPLETED summary; a degraded run publishes exitCode!=0 (timeout) OR 0 (caps), so key
            // the status off the flag, not the exit code.
            const isDegraded = !!(s && s.degraded);
            const exitOk = !(typeof s.exitCode === 'number' && s.exitCode !== 0);
            // COST PARITY (run-total): give the builder row the SAME ``usage`` chip a worker
            // carries, derived from its own streamed cc-usage events. ``computeRunTotal`` already
            // sums ``row.usage`` for every agent — builders were the only type missing it, so the
            // header total read too low. Reuse the row's existing usage if a prior phase set one.
            const rosterId = reducerRosterKey(agentId);   // f.synth merge: land on __reducer__root
            const prevRow = roster[rosterId] || {};
            // COST SOURCE PRECEDENCE:
            //   1. ``s.usage`` — the AUTHORITATIVE token totals the builder metered (the same
            //      numbers that drive billing), carried on the completion event. Correct on
            //      SUCCESS and FAILURE alike, even when no cc-usage lines streamed.
            //   2. a usage chip a prior phase already set on the row.
            //   3. reconstruct from streamed cc-usage lines (fragile — misses on failed/
            //      background runs, which is why the header showed only the router's cents).
            const builderUsage = (s && typeof s.usage === 'object' && s.usage)
              ? withBuilderCost(s.usage)   // authoritative token chip → enrich with cost/points
              : (prevRow.usage
                 || builderUsageFromLines(prevRow.builderLines, s.model));
            upsertAgentRow(roster, rosterId, {
              label: agentId,
              status: isDegraded ? 'degraded' : (exitOk ? 'done' : 'failed'),
              isBuilder: true,
              // Carry the subtype too so the roster can distinguish a USER STOP ('user_cancelled',
              // B2/B6) from a governed policy-cap degrade — rendered as "Stopped" vs "degraded".
              ...(isDegraded ? { degraded: true, degradedReason: String(s.degradedReason || ''),
                                 degradedSubtype: String(s.degradedSubtype || '') } : {}),
              // Attach the completion (logKey/eventsKey in S3) so a DONE builder's shared-container
              // view can render BuilderLogCard (the full transcript replay), not just a summary.
              builderCompletion: { ...s, agentId, ts: phaseTs },
              ...(builderUsage ? { usage: builderUsage } : {}),
            });
          }
          const doneRunId = typeof p.runId === 'string' ? p.runId : (turn.builderRunId || '');
          if (doneRunId && pendingBuilderWaits.has(doneRunId)) pendingBuilderWaits.delete(doneRunId);
        } else if (typeof p.agentId === 'string' && p.agentId.trim()) {
          // A non-terminal phase from a known builder agent → mark it a running builder so the
          // room shows the Stop control (not the awaiting band) on its row.
          // (f.synth merge: reduce display ids land on the existing __reducer__root row.)
          upsertAgentRow(roster, reducerRosterKey(p.agentId.trim()), {
            label: p.agentId.trim(), status: 'running', isBuilder: true,
            runId: (typeof p.runId === 'string' && p.runId) ? p.runId : undefined,
          });
        }
        break;
      }
      case AGENT_EVENTS.PLAN_UPDATE_RUNNING: {
        // ASYNC PLANNER as a first-class tree row (user ask 2026-09-19): the Planner is NOT part of
        // the network's state function — it's the fire-and-forget plan-detail lambda f.synth kicks.
        // Seed its roster row the moment the beacon fires: root-level, ranked AFTER "Network
        // Summarizer" (agentTree siblingRank), SPINNING while the Summarizer streams its synthesis —
        // honest "non-blocking work in flight". The container's live builder events (agentId
        // "Planner") then attach their terminal lines to this same row.
        // RUN-SCOPED (2026-09-22, the "Planner starts done" bug): the beacon now carries its run
        // (correlationId/executionId/payload.runId, stamped by the engine — verified equal to the
        // room's currentRunId, which is EXECUTION_STARTED's id). A beacon from a PRIOR run — or an
        // UNSTAMPED legacy one replayed from history — must NOT create/settle a Planner row in THIS
        // run's tree (the stale prior-run "Planner done" leaking in at mount). When currentRunId is
        // unknown (a historical room with no EXECUTION_STARTED) we can't scope, so honor the beacon.
        if (_planBeaconOffRun(env, p, currentRunId)) break;
        upsertAgentRow(roster, 'Planner', {
          label: 'Planner', status: 'running', isBuilder: true, isPlanner: true,
          heartbeatText: 'updating the plan…',
        });
        break;
      }
      case AGENT_EVENTS.PLAN_UPDATED: {
        // Terminal beacon (done or failed) — the ONLY thing that settles the Planner row; the
        // planner's per-batch CLAUDE_CODE completed phases must NOT (it runs several batches).
        // RUN-SCOPED (see PLAN_UPDATE_RUNNING): ignore a prior-run / unstamped beacon so a stale
        // terminal doesn't settle a phantom "done" Planner into the current run. The EXISTING-row
        // guard stays as belt-and-suspenders (a fresh roster has no Planner to settle).
        if (_planBeaconOffRun(env, p, currentRunId)) break;
        if (roster.Planner) {
          upsertAgentRow(roster, 'Planner', {
            status: (String(p.status || '').toLowerCase() === 'failed') ? 'failed' : 'done',
            heartbeatText: undefined,
          });
        }
        break;
      }
      case AGENT_EVENTS.BUILDER_INPUT_WAIT: {
        const turn = turnForLogAppend(env);
        const waitRunId = typeof p.runId === 'string' ? p.runId : '';
        const waitAgentId = typeof p.agentId === 'string' ? p.agentId.trim() : '';
        const wait = {
          runId: waitRunId,
          prompt: typeof p.prompt === 'string' ? p.prompt : '',
          eventId: env.eventId || '',
        };
        // Legacy single-wait field kept for existing consumers (last-wins).
        turn.builderInputWait = wait;
        // MULTI-WAIT: register this pause so the room can show every awaiting builder at once
        // (not just the latest). Keyed by runId — the matching BUILDER_INPUT_RESOLVED clears it.
        if (waitRunId) {
          pendingBuilderWaits.set(waitRunId, {
            agentId: waitAgentId || undefined,
            runId: waitRunId,
            waitEventId: env.eventId || '',
            prompt: wait.prompt,
            interaction: (p.interaction && typeof p.interaction === 'object')
              ? p.interaction : undefined,
            turnId: turn.id,
          });
        }
        // ROSTER: this builder is now awaiting the user. isBuilder=true drives the Stop-vs-band
        // control choice in the room. Row APPEARS here if it hadn't streamed anything yet.
        if (waitAgentId) {
          upsertAgentRow(roster, waitAgentId, {
            label: waitAgentId, status: 'awaiting', isBuilder: true, runId: waitRunId || undefined,
          });
        }
        break;
      }
      case AGENT_EVENTS.BUILDER_INPUT_RESOLVED: {
        // A wait was answered (or the run resolved it) → drop it from the pending set and flip its
        // roster row back to running. RESOLVED carries only runId (no agentId), so we join on runId.
        const resolvedRunId = typeof p.runId === 'string' ? p.runId : '';
        if (resolvedRunId && pendingBuilderWaits.has(resolvedRunId)) {
          const cleared = pendingBuilderWaits.get(resolvedRunId);
          pendingBuilderWaits.delete(resolvedRunId);
          if (cleared && cleared.agentId) {
            upsertAgentRow(roster, cleared.agentId, { status: 'running' });
          }
        }
        break;
      }
      case AGENT_EVENTS.LOG_BATCH:
      case AGENT_EVENTS.STATUS_UPDATE:
      case AGENT_EVENTS.AGENT_TOOL_CALL:
      case AGENT_EVENTS.AGENT_REASONING:
      case AGENT_EVENTS.SFN_TRANSITION: {
        const lines = envelopeToThinkingLines(env);
        if (!lines.length) break;
        const turn = turnForLogAppend(env);
        turn.thinking.push(...lines);
        // Track the most recent agentId we saw so MiniFlow can pulse the
        // right node. We only update on lines that carry an agent — a
        // workflow-level SFN_TRANSITION doesn't change the active agent.
        for (let k = lines.length - 1; k >= 0; k -= 1) {
          if (lines[k].agentId) {
            setActiveAgent(turn, lines[k].agentId);
            upsertAgentRow(roster, lines[k].agentId,
              { label: lines[k].agentId, status: 'running' });
            break;
          }
        }
        // ATTRIBUTED REASONING (lead routing rationale / node-reducer judge verdict): when an
        // AGENT_REASONING carries an agentId, accrete its text onto that roster row (in addition
        // to the ambient thinking line above) so the room can render a "why these agents / routing
        // rationale" block on the owning agent's row. The reasoning line itself carries no agentId,
        // so read it off the payload. ``stage`` distinguishes lead vs reducer for the UI.
        if (et === AGENT_EVENTS.AGENT_REASONING) {
          const rid = typeof p.agentId === 'string' ? p.agentId.trim() : '';
          const rtext = typeof p.text === 'string' ? p.text.trim() : '';
          const _stage = typeof p.stage === 'string' ? p.stage : '';
          if (rid && rtext) {
            const existing = (roster[rid] && Array.isArray(roster[rid].reasoning))
              ? roster[rid].reasoning : [];
            // Dedup on text so reconnect replay doesn't stack duplicates.
            if (!existing.some((r) => r.text === rtext)) {
              upsertAgentRow(roster, rid, {
                label: rid,
                reasoning: [...existing, { stage: _stage, text: rtext }],
              });
            }
          }
          // ENGINE-STAGE FINALIZE: this reasoning line IS the output of the Lead/Reducer stage row
          // seeded by its AGENT_STARTED (agentName ``__{stage}__{nodePath}``). Flip that synthetic
          // row running→done and load the rationale as its ``text`` so clicking it shows the reasoning
          // in the reader ("Lead Reasoning" / "Reasoning Summarizer"). SELF-SUFFICIENT: derive the
          // clean label ("Network Lead"/"Lead"/"Summarizer") + tree placement (nest under the owning
          // node) from nodePath here — the live start-beat may not have landed (history/reconnect),
          // so we must not depend on it. Also stamp the per-call usage so the line shows model + $
          // like an agent row. Independent of the owner-row accretion above.
          if ((_stage === 'lead' || _stage === 'reducer') && rtext) {
            const _np = (typeof p.nodePath === 'string' && p.nodePath.trim()) ? p.nodePath.trim() : 'root';
            const _stageId = `__${_stage}__${_np}`;
            const _place = engineStagePlacement(_stage, _np);
            const _su = (p.usage && typeof p.usage === 'object') ? p.usage : undefined;
            upsertAgentRow(roster, _stageId, {
              status: 'done',
              text: rtext,
              isEngineStage: true,
              stageKind: _stage,
              label: _place.label,
              parentAgentId: _place.parentAgentId,
              level: _place.level,
              nodePath: _np,
              ...(_su ? { usage: _su } : {}),
            });
          }
          // ENGINE-STAGE COST: sum this reasoning call's usage into the run's lead/reducer bucket.
          // Keyed by (runId, stage); deduped by eventId so a reconnect replay can't double-count.
          const _stg = typeof p.stage === 'string' ? p.stage : '';
          const _u = (p.usage && typeof p.usage === 'object') ? p.usage : null;
          if (_u && (_stg === 'lead' || _stg === 'reducer')) {
            const _rid = turn.runId || currentRunId || env.executionId || env.correlationId;
            const _eid = String(env.eventId || '');
            if (_rid) {
              if (!stageUsageByRun[_rid]) stageUsageByRun[_rid] = { lead: null, reducer: null, _seen: new Set() };
              const bucket = stageUsageByRun[_rid];
              if (!_eid || !bucket._seen.has(_eid)) {
                if (_eid) bucket._seen.add(_eid);
                const prev = bucket[_stg] || { cost: 0, points: 0, calls: 0, modelId: '' };
                bucket[_stg] = {
                  cost: prev.cost + (Number(_u.estimatedCostUsd) || 0),
                  points: prev.points + (Number(_u.points) || 0),
                  calls: prev.calls + (Number(_u.calls) || 1),
                  // Keep the model id (all calls of a stage use the same tier); last non-empty wins.
                  modelId: _u.modelId || prev.modelId,
                };
              }
            }
          }
        }
        break;
      }
      case AGENT_EVENTS.SUMMARY_DELTA: {
        // FINAL SYNTHESIS streaming: append this chunk to the Summary row's text so the block
        // composes LIVE (no static lull at the climax). Keep the row ``running`` — the authoritative
        // AGENT_FINAL_RESPONSE(Summary) later flips it ``done`` and REPLACES the text with the clean
        // full answer (so a mid-stream refresh never sticks). Deltas are ephemeral (not persisted),
        // so history rehydrate simply never sees them → shows the final text only.
        const chunk = typeof p.text === 'string' ? p.text : '';
        const statusText = typeof p.status === 'string' ? p.status.trim() : '';
        ensureLiveTurn(env);
        {
          const prev = roster.Summary;
          // Don't clobber an already-finalized Summary (a late delta after the final response).
          if (!prev || prev.status !== 'done') {
            if (chunk) {
              // Answer CHUNK: accrete into the live preview; a real token supersedes any status label.
              const prior = (prev && typeof prev.streamText === 'string') ? prev.streamText : '';
              const nextText = prior + chunk;
              upsertAgentRow(roster, 'Summary', {
                label: 'Summary', status: 'running',
                streamText: nextText,   // marker that this is a live-composed preview, not the record
                text: nextText,
                summaryStatus: undefined,
              });
            } else if (statusText) {
              // STATUS beat (silent-phase label, no answer yet) — advance the header only. Don't
              // touch ``text`` (there may be none yet); the composing indicator reads summaryStatus.
              upsertAgentRow(roster, 'Summary', {
                label: 'Summary', status: 'running', summaryStatus: statusText,
              });
            }
          }
        }
        break;
      }
      default:
        break;
    }
  }

  // Splice optimistic user bubbles in chronological order. They render
  // immediately when the user clicks Send and disappear once the backend
  // echoes a USER_PROMPT with the matching correlationId.
  if (optimisticByCorrelation.size > 0) {
    /** @type {FeedEntry[]} */
    const bubbles = [];
    for (const [corr, entry] of optimisticByCorrelation) {
      if (reconciledOptimisticIds.has(entry.id)) continue;
      const bubble = {
        kind: 'user',
        id: entry.id,
        correlationId: corr,
        actorName: entry.actorName,
        text: entry.text,
        ts: entry.ts,
      };
      bubbles.push(bubble);
      // Best-effort: also surface the optimistic bubble inside the matching
      // live turn so the turn card renders immediately. The reconciliation
      // pass when the backend echoes a USER_PROMPT will replace the entry
      // in-place via the same correlationId match.
      const turn = turnGroups.find((t) => t.correlationId === corr);
      if (turn && !turn.userEntry) turn.userEntry = bubble;
      else if (!turn && !liveTurn) {
        openTurn({ id: bubble.id, correlationId: corr, userEntry: bubble, startedAt: bubble.ts });
      }
    }
    if (bubbles.length) {
      feed.push(...bubbles);
      feed.sort(byTimestamp);
    }
  }

  const activeAgentId = liveTurn && liveTurn.status === 'live' ? liveTurn.activeAgentId : undefined;
  // ``completedAgentIds`` reflects the live turn (or the most recent
  // turn when nothing is live). MiniFlow paints these as "done" while
  // ``activeAgentId`` keeps pulsing — together the user sees the run's
  // forward progress through the network instead of just one node.
  const refTurn = liveTurn || (turnGroups.length > 0 ? turnGroups[turnGroups.length - 1] : null);
  const completedAgentIds = refTurn && refTurn.completedAgentIds instanceof Set
    ? Array.from(refTurn.completedAgentIds)
    : [];

  // The pending-waits list the room binds its "N awaiting" notice + interaction band to. Ordered
  // by insertion (Map preserves it) so "select the oldest first" is the natural default.
  const pendingBuilderWaitList = Array.from(pendingBuilderWaits.values());

  // Run-level roster: the map (keyed by agentId, for O(1) lookup) plus a stable array ordered by
  // (level, first-seen seq) so a flat render already reads top-down; the room builds the tree from
  // parentAgentId. Kept out of ``turnGroups`` because the tree spans the whole run, not one turn.
  // SETTLE STRANDED SPINNERS (LESSONS L3 / bridge-miss): a run whose turn is no longer ``live`` cannot
  // still have a "working" lead/agent — but if its terminal beacon was never published (dropped
  // EXECUTION_COMPLETED, or a historical run with no terminal UI_STATE) ``closeAllLiveTurns`` never
  // fired, so engine-stage rows (``__lead__``/``__reducer__``) + agents are stuck ``running``/``awaiting``
  // FOREVER. Post-pass: for every run with NO live turn, flip lingering running/awaiting → done. A
  // genuinely-live run (its turn still ``live``) is untouched, so real in-flight spinners keep spinning.
  {
    const _liveRunIds = new Set(turnGroups.filter((t) => t.status === 'live').map((t) => t.runId));
    // Is the CURRENT run actually TERMINAL? A generative fan-out shares ONE turn and closes it on the
    // FIRST agent's AGENT_FINAL_RESPONSE (turn.status='completed' above), so the turn reads
    // "completed" while sibling agents are STILL running (e.g. a 3-min reasoner). Settling on
    // "turn not live" alone therefore flipped those siblings to ``done`` mid-run — the per-node
    // done-early the whole live-network view exists to avoid (frank-beaver-gizmo). Only a REAL run
    // terminal (terminal UI_STATE, or the top-level EXECUTION_COMPLETED/FAILED) settles the CURRENT
    // run's stragglers; an agent otherwise stays ``running`` until its OWN AGENT_FINAL_RESPONSE. OLD
    // runs (rid ≠ currentRunId) still settle unconditionally (historical, no live spinner to protect).
    const _curTerminal = _TERMINAL_UI_STATES.has(String(uiState || '').toUpperCase())
      || (lastExecution && (lastExecution.status === 'COMPLETED' || lastExecution.status === 'FAILED'));
    for (const rid of Object.keys(rostersByRun)) {
      if (_liveRunIds.has(rid)) continue;
      // The CURRENT run only settles once it is genuinely done: a real terminal (UI_STATE/exec) OR its
      // Summary landed (beacon-miss resilience). A non-Summary agent final does NOT count — that is the
      // generative fan-out case where siblings still run (L19). OLD runs settle unconditionally.
      if (rid === currentRunId && !_curTerminal && !summaryFinaledRunIds.has(rid)) continue;
      const r = rostersByRun[rid];
      for (const id of Object.keys(r)) {
        // Planner excepted — it runs OUTSIDE the SFN; only PLAN_UPDATED settles it (see
        // closeAllLiveTurns). A terminal exec/UI_STATE must not flip it to a premature "done".
        if (r[id] && !r[id].isPlanner
            && (r[id].status === 'running' || r[id].status === 'awaiting')) {
          r[id].status = 'done';
        }
      }
    }
  }
  const _sortRoster = (m) => Object.values(m).slice().sort(
    (a, b) => ((a.level ?? 1) - (b.level ?? 1)) || ((a.seq ?? 0) - (b.seq ?? 0)),
  );
  const agentRoster = _sortRoster(roster);
  // PER-RUN rosters as sorted arrays {runId → AgentRosterRow[]} so the room renders one collapsible
  // block per run, each on its OWN topology (via runReleaseVersions[runId]). The current run is
  // ``agentRoster`` above; this map covers all runs (including the current one).
  const agentRostersByRun = {};
  for (const rid of Object.keys(rostersByRun)) {
    agentRostersByRun[rid] = _sortRoster(rostersByRun[rid]);
  }

  // Serialize the engine-stage cost map, dropping the internal ``_seen`` dedup Set → plain
  // {runId → {lead, reducer}} the room reads for the "All Leads" / "All Reducers" cost lines.
  const stageCostByRun = {};
  for (const rid of Object.keys(stageUsageByRun)) {
    const b = stageUsageByRun[rid] || {};
    stageCostByRun[rid] = { lead: b.lead || null, reducer: b.reducer || null };
  }

  return {
    feed, turnGroups, uiState, pendingApproval, lastExecution,
    activeAgentId, completedAgentIds,
    pendingBuilderWaits: pendingBuilderWaitList,
    agents: roster, agentRoster, currentRunId,
    currentRunReleaseVersion, runReleaseVersions, agentRostersByRun, stageCostByRun,
  };
}

function byTimestamp(a, b) {
  const ta = a && a.ts ? Number(typeof a.ts === 'string' ? Date.parse(a.ts) : a.ts) : 0;
  const tb = b && b.ts ? Number(typeof b.ts === 'string' ? Date.parse(b.ts) : b.ts) : 0;
  if (!Number.isFinite(ta) || !Number.isFinite(tb)) return 0;
  return ta - tb;
}
